'use client';

import { HTMLAttributes, useActionState, useEffect, useRef } from 'react';
import { Input, Textarea } from '@heroui/react';
import { gtag } from '@/utils/gtagPush';
import { usePathname } from 'next/navigation';
import { hotjar } from 'react-hotjar';
import { usePlausible } from 'next-plausible';
import { sendEmail } from '@/lib/email-actions';
import LeviButton from '@/app/components/LeviButton';
import FormAntiSpamFields from '@/app/components/FormAntiSpamFields';

interface Props extends HTMLAttributes<HTMLDivElement>
{
}

const initialState = {
  error: undefined as string | undefined,
  success: false,
};

export default function ContactForm({ className }: Props) {
  const [state, formAction] = useActionState(sendEmail, initialState);
  const gaEventSent = useRef(false);

  const plausible = usePlausible();

  const currentPath = usePathname();

  useEffect(() => {
    if (state?.success && !gaEventSent.current)
    {
      gtag('event', 'generate_lead', { event_category: 'contact_form', event_label: currentPath, value: 1 });
      if (hotjar.initialized())
      {
        hotjar.event('successfully_submitted_form');
      }
      plausible('contact_form_submit', { props: { path: currentPath } });
      gaEventSent.current = true;
    }
  }, [currentPath, plausible, state?.success]);

  return (
    <div className={ `${ className } text-center md:text-left` }>
      {
        state?.success ? <div className={ 'mt-4 text-xl' }><p className={ 'text-green-700' }>Vaši zprávu jsme dostali. 🫡 Co nejdříve se vám ozveme.</p></div>
          : <form className={ 'w-full relative' } action={ formAction } autoComplete={ 'on' } name={ 'contact' }>
            <Input type={ 'text' }
                   data-hj-allow
                   variant={ 'bordered' }
                   required
                   isRequired
                   radius={'sm'}
                   className={ 'w-full' }
                   color={ 'default' }
                   label={ 'Celé jméno' }
                   name={ 'name' }
                   autoComplete={ 'name' }
                   classNames={ {
                     inputWrapper: 'border-black border-2 hover:border-black/80! transition!',
                   } }
                   onFocus={ () => {
                     hotjar.event('main-contact_form-focused_name_input');
                     plausible('contact_form-focused_name_input', { props: { path: currentPath } });
                   } } />
            <div className={ 'mt-2 flex flex-col md:flex-row gap-2' }>
              <Input type={ 'email' } data-hj-allow variant={ 'bordered' } color={ 'default' } radius={'sm'} required isRequired label={ 'E-mail' } autoComplete={'email'} classNames={ {
                inputWrapper: 'border-black border-2 hover:border-black/80! transition!',
              } } className={ 'w-full' } name={ 'email' } onFocus={ () => {
                hotjar.event('main-contact_form-focused_email_input');
                plausible('contact_form-focused_email_input', { props: { path: currentPath } });
              } } />

              <Input type={ 'tel' } data-hj-allow variant={ 'bordered' } required={ false } color={ 'default' } radius={'sm'} label={ 'Telefon' } className={ 'w-full' } name={ 'phone' } autoComplete={'phone'} classNames={ {
                inputWrapper: 'border-black border-2 hover:border-black/80! transition!',
              } } onFocus={ () => {
                hotjar.event('main-contact_form-focused_phone_input');
                plausible('contact_form-focused_phone_input', { props: { path: currentPath } });
              } } />
            </div>
            <Textarea placeholder={ '' } data-hj-allow variant={ 'bordered' } label={ 'Zpráva' } color={ 'default' } radius={'sm'} className={ 'w-full mt-2' } name={ 'message' } classNames={ {
              inputWrapper: 'border-black border-2 hover:border-black/80! transition!',
            } } onFocus={ () => {
              hotjar.event('main-contact_form-focused_message_input');
              plausible('contact_form-focused_message_input', { props: { path: currentPath } });
            } } />
            <FormAntiSpamFields resetKey={ state } />
            <LeviButton color={'black'} type={'submit'} className={'mt-4 w-full!'} onClick={() => {
              hotjar.event('main-contact_form-clicked_send');
              plausible('contact_form-clicked_send', { props: { path: currentPath } });
            }}>Odeslat</LeviButton>
            { state?.error && <p className={ `mt-4 text-danger` }>{ state.error }</p> }
          </form>
      }
    </div>
  );
}
